/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [media-rich-editor.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 24.02.20, 14:09
 */

import Sortable from 'sortablejs';
import axios from 'axios';

import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import Dropzone from 'react-dropzone';
import EmojiEditor from '../emoji-editor/emoji-editor';
import SoundcloudPlayer from "../media-players/soundcloud-player";
import {asyncValidateAuth} from "../../actions";
import TextEditor from "../emoji-editor/text-editor";
import FormUpload from "./form-upload";
import {useLanguage, useLocalSiteConfiguration} from "../configuration/configuration";
import {Collapse} from 'bootstrap/dist/js/bootstrap';
import {Cancel, ImageSearch} from "@material-ui/icons";
import {getYoutubeThumbnail} from "../../actions/environment";
import {notifyWarning} from "../notifications/notify";
import {isSupportedVideoType} from "../dropzone-uploader/file-uploader";
import {useAuthorization} from "../../selectors";

const ONE_MEGABYTE = 1000000;  // 1 MB in bytes

const MediaRichEditor = ({className, id, setIsOpen, text, rawmode, username, callback}) => {
    const [accepted, setAccepted] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [embedded, setEmbedded] = useState([]);
    const [processing, setProcessing] = useState(false);
    const mediaPreviewRef = useRef(null);
    const mediaUploadRef = useRef(null);
    const videoUploadRef = useRef(null);
    const youTubeRef = useRef(null);
    const vimeoRef = useRef(null);
    const soundCloudRef = useRef(null);
    const {isSuperUser} = useAuthorization();

    const Lang = useLanguage();
    const dispatch = useDispatch();
    const {videoUpload, videoUploadMaxMB=20, videoUploadAdminOnly} = useLocalSiteConfiguration();

    useEffect(() => {
        if (mediaPreviewRef) {
            Sortable.create(mediaPreviewRef.current, {animation: 150});
        }
        if (mediaUploadRef) {
            mediaUploadRef.current.collapse = new Collapse(mediaUploadRef.current, {toggle: false})
            videoUploadRef.current.collapse = new Collapse(videoUploadRef.current, {toggle: false})
            youTubeRef.current.collapse = new Collapse(youTubeRef.current, {toggle: false})
            vimeoRef.current.collapse = new Collapse(vimeoRef.current, {toggle: false})
            soundCloudRef.current.collapse = new Collapse(soundCloudRef.current, {toggle: false})
        }
        return () => {
            accepted.forEach(file => window.URL.revokeObjectURL(file.preview));
        }
    }, [id]);


    const renderImagesPreview = () => {
        // just a hack for doing authorization refresh before file upload
        accepted.length > 0 && dispatch(asyncValidateAuth(username));

        return accepted.map((file, idx) => {
            return <div key={file.name} className='media-upload-item' data-position={idx}>

                {isSupportedVideoType(file.type) ?
                    <video src={file.preview}></video> : <img src={file.preview}/>}

                <span className='media-upload-icon' onClick={() => removeAccepted(file)}>
                    <Cancel fontSize='small'/>
                </span>
            </div>;
        });
    }

    const getVimeoThumbnail = embeddedFile => {
        if (processing === true) return;

        const videoid = embeddedFile.url.match(/(?:https?:\/{2})?(?:w{3}\.)?vimeo(?:be)?\.(?:com)(?:\/watch\?v=|\/)([^\s&]+)/);
        setProcessing(true);

        axios.get(`http://vimeo.com/api/v2/video/${videoid[1]}.json`)
        .then(response => {
            embeddedFile.thumbnail = response.data[0].thumbnail_large;
            setEmbedded([embeddedFile])
            setProcessing(false);
        });
    }

    // video preview handled by renderImagesPreview
    const renderVideoPreview = () => {
        const embeddedFile = embedded[0];

        if (embeddedFile.type === 'YOUTUBE') {
            embeddedFile.thumbnail = getYoutubeThumbnail(embeddedFile.url);
            return <div className='youtube-preview'>
                <div className='media-upload-item'>
                    <img src={embeddedFile.thumbnail}/>
                    <span className='media-upload-icon'
                          onClick={() => removeEmbedded(embeddedFile)}>
                        <Cancel fontSize='small'/>
                    </span>
                </div>
            </div>
        } else if (embeddedFile.type === 'VIMEO') {
            if (embeddedFile.thumbnail === undefined || embeddedFile.thumbnail.length === 0) {
                getVimeoThumbnail(embeddedFile);
                return <div>Loading..</div>
            }
            return <div className='vimeo-preview'>
                <div className='media-upload-item'>
                    <img src={embedded.thumbnail}/>
                    <span className='media-upload-icon'
                          onClick={() => removeEmbedded(embeddedFile)}>
                        <Cancel fontSize='small'/>
                    </span>
                </div>
            </div>

        } else if (embeddedFile.type === 'SOUNDCLOUD') {
            return <SoundcloudPlayer url={embeddedFile.url}/>;
        }
    }

    const renderPreview = () => {
        if (embedded.length > 0) {
            return renderVideoPreview();
        }
        return renderImagesPreview();
    }

    const removeAccepted = file => {
        const acceptedFiles = accepted.filter(entry => {
            return (entry.name === file.name) ? null : entry;
        });

        window.URL.revokeObjectURL(file.preview);
        setAccepted(acceptedFiles);
    }

    const removeEmbedded = file => {
        const embeddedFiles = embedded.filter(entry => {
            return (entry.url === file.url) ? null : entry;
        });

        setEmbedded(embeddedFiles);
    }

    const handleOnDropFiles = (acceptedDrop, rejectedDrop) => {
        const acceptedFiles = Object.assign([], accepted);

        /* create preview */
        acceptedDrop.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        })).forEach(file => acceptedFiles.push(file));

        setAccepted(acceptedFiles);
        setRejected([]);
        setEmbedded([]);
    };

    // among other things creates a preview url into the file object
    const handleOnDropVideos = (acceptedDrop, rejectedDrop) => {
        const acceptedFiles = Object.assign([], accepted);

        if(acceptedDrop.length > 1) {
            notifyWarning(Lang.post.editor.warningUploadOnlyOne);
            return;
        }

        /* create preview url's */
        acceptedDrop
        .filter(file => {
            if(file.size > videoUploadMaxMB * ONE_MEGABYTE) {
                notifyWarning(Lang.post.editor.warningUploadTooBig(videoUploadMaxMB))
                return false;
            }
            if(!videoUpload) {
                notifyWarning(Lang.post.editor.warningVideoUploadDisabled)
                return false;
            }
            return true;
        })
        .map(file => Object.assign(file, {preview: URL.createObjectURL(file)}))
        .forEach(file => acceptedFiles.push(file));

        setAccepted(acceptedFiles);
        setRejected([]);
        setEmbedded([]);
    };

    const handleTextAreaEnter = (text) => {
        const orderedFiles = [];

        /* reorder images if necessary */
        const children = mediaPreviewRef.current.children;
        [...children].forEach(child => {
            orderedFiles.push(accepted[child.dataset.position]);
        });

        // visibility will be taken from local storage and is handled by post-visibility-popover
        callback(text, orderedFiles, embedded);

        accepted.forEach(file => window.URL.revokeObjectURL(file.preview));
        setAccepted([]);
        setRejected([]);
        setEmbedded([]);

        setIsOpen && setIsOpen(false);
    };

    const handleFormUpload = (url, type) => {
        if (url != null && url.length > 0) {
            accepted.forEach(file => window.URL.revokeObjectURL(file.preview));

            setAccepted([]);
            setRejected([]);
            setEmbedded([{id: 0, thumbnail: '', url: url, type: type}]);
        }
    };

    const toggle = element => {
        mediaUploadRef.current.collapse.hide();
        videoUploadRef.current.collapse.hide();
        youTubeRef.current.collapse.hide();
        vimeoRef.current.collapse.hide();
        soundCloudRef.current.collapse.hide();

        element.current.collapse.toggle();
    }

    const handleMediaUpload = event => {
        event.preventDefault();
        toggle(mediaUploadRef);
    }

    const handleYoutubeUpload = event => {
        event.preventDefault();
        toggle(youTubeRef);
    }

    const handleVimeoUpload = event => {
        event.preventDefault();
        toggle(vimeoRef);
    }

    const handleSoundCloudUpload = event => {
        event.preventDefault();
        toggle(soundCloudRef);
    }

    const handleVideoUpload = event => {
        event.preventDefault();
        toggle(videoUploadRef);
    }

    // enable video upload only if configured to true
    const allowVideoUpload = videoUpload || (videoUploadAdminOnly && isSuperUser);
    return <div className={`media-rich-editor ${className ? className : ''}`}>
        {!rawmode && <EmojiEditor id={`editable-box-${id}`} text={text}
                                  callback={handleTextAreaEnter}
                                  mediaupload={handleMediaUpload}
                                  videoUpload={allowVideoUpload ? handleVideoUpload : null}
                                  soundcloud={handleSoundCloudUpload}
                                  youtube={handleYoutubeUpload}/>}

        {rawmode && <TextEditor id={`editable-box-${id}`} text={text}
                                callback={handleTextAreaEnter}
                                mediaupload={handleMediaUpload}
                                videoUpload={handleVideoUpload}
                                soundcloud={handleSoundCloudUpload}
                                youtube={handleYoutubeUpload}/>}

        <div className='media-upload-preview' ref={mediaPreviewRef}>
            {renderPreview()}
        </div>

        <div className="collapse" ref={mediaUploadRef}>
            <Dropzone className='media-upload-zone'
                      accept="image/jpeg, image/png, image/gif, image/svg+xml"
                      onDrop={handleOnDropFiles}>
                <div className='centered-box flex-column w-100 mt-2 mb-4'>
                    <div
                        className='text-center media-upload-text mb-3'>{Lang.post.editor.uploadImagePlaceholder}</div>
                    <span><ImageSearch fontSize='large' style={{transform: 'scale(2.0)'}}/></span>
                </div>

            </Dropzone>
        </div>

        <div className="collapse" ref={videoUploadRef}>
            <Dropzone className='media-upload-zone'
                      accept="video/mp4, video/mpeg"
                      onDrop={handleOnDropVideos}>
                <div className='centered-box flex-column w-100 mt-2 mb-4'>
                    <div
                        className='text-center media-upload-text mb-3'>{Lang.post.editor.uploadVideoPlaceholder}</div>
                    <span><ImageSearch fontSize='large' style={{transform: 'scale(2.0)'}}/></span>
                </div>

            </Dropzone>
        </div>

        <div className="collapse" ref={youTubeRef}>
            <FormUpload type="YOUTUBE"
                        placeholder={Lang.post.editor.youTubePlaceholder}
                        pattern="^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+"
                        callback={handleFormUpload}
            />
        </div>

        <div className="collapse" ref={vimeoRef}>
            <FormUpload type="VIMEO"
                        placeholder={Lang.post.editor.vimeoPlaceholder}
                        pattern="^(http(s)?:\/\/)?((w){3}.)?vimeo?(\.com)?\/.+"
                        callback={handleFormUpload}
            />
        </div>

        <div className="collapse" ref={soundCloudRef}>
            <FormUpload type="SOUNDCLOUD"
                        placeholder={Lang.post.editor.soundcloudPlaceholder}
                        pattern="^(https?:\/\/)?(www.)?(m\.)?soundcloud\.com\/[\w\-\.]+(\/)+[\w\-\.]+/?$"
                        callback={handleFormUpload}
            />
        </div>

    </div>
}

export default MediaRichEditor;